import { Close, IconButton } from '@fountain/fountain-ui-components';
import { Typography } from '@material-ui/core';
import complianceAppIcon from 'images/compliance-app-icon.svg';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';

import { CueCtaButton } from 'components/CueCtaButton';
import { usePostHog } from 'hooks/usePostHog';

import { CUE_COMPLIANCE_UPSELL_CTA } from './analytics-events';
import messages from './messages';
import { useStyles } from './styles';

export interface ComplianceUpsellStageCardProps {
  openingTitle: string;
  stageTitle: string;
  /**
   * Whether the Cue CTA can be offered. Clicking it is only meaningful when the
   * Cue panel is actually mounted for this account, so when this is false the
   * card renders as awareness-only rather than showing a button that would do
   * nothing.
   */
  showDiscoverCta: boolean;
}

// Promo card for the Compliance upsell, matching the Figma handoff
// (node 12381:195402). Rendered directly below an eligible data-collection stage
// in the workflow editor's stage list; StageColumn owns the gating
// (`show_compliance_upsell` on the stage plus the `wx-compliance-upsell` flag).
//
// "Discover" opens the Cue panel with a prompt naming this workflow and stage.
// Everything after that — matching the file questions to compliance document
// types, asking for consent, and transforming the workflow — belongs to the
// Compliance Upsell Agent (CMP-1810), not to this component.
//
// Dismissal is per-render only and intentionally not persisted; if it should
// survive a reload that needs a real preference store.
export const ComplianceUpsellStageCard: React.FC<
  ComplianceUpsellStageCardProps
> = ({ openingTitle, stageTitle, showDiscoverCta }) => {
  const classes = useStyles();
  const intl = useIntl();
  const posthog = usePostHog();
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) {
    return null;
  }

  const handleDismiss = () => {
    posthog.capture(CUE_COMPLIANCE_UPSELL_CTA.closeEvent, {
      cta_id: CUE_COMPLIANCE_UPSELL_CTA.ctaId,
    });
    setDismissed(true);
  };

  return (
    <div className={classes.card}>
      <img alt="" className={classes.icon} src={complianceAppIcon} />

      <div className={classes.content}>
        <div className={classes.headerRow}>
          <div>
            <Typography className={classes.title} variant="body1">
              {intl.formatMessage(messages.title)}
            </Typography>
            <Typography className={classes.subtitle} variant="body2">
              {intl.formatMessage(messages.subtitle)}
            </Typography>
          </div>

          <IconButton
            aria-label={intl.formatMessage(messages.dismissAriaLabel)}
            className={classes.dismissButton}
            onClick={handleDismiss}
            size="small"
          >
            <Close />
          </IconButton>
        </div>

        {showDiscoverCta && (
          <div className={classes.discoverButtonWrapper}>
            <CueCtaButton
              analytics={CUE_COMPLIANCE_UPSELL_CTA}
              hideMessage
              message={intl.formatMessage(messages.discoverCuePrompt, {
                openingTitle,
                stageTitle,
              })}
            >
              {intl.formatMessage(messages.discover)}
            </CueCtaButton>
          </div>
        )}
      </div>
    </div>
  );
};
